import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, useElements, useStripe } from "@stripe/react-stripe-js";
import { doc, onSnapshot } from "firebase/firestore";
import {
  clearActiveFirmsCache,
  firebaseHelper,
  persistActiveWorkspace,
  publishableKey,
} from "ui";
import { AuthContext } from "../../auth/Auth";
import { useContext } from "react";
import { createTeamSubscriptionAPIUrl } from "../../stripe/config";
import { sendExceptionToSentry } from "../../libs/sentry";
import { useStripeStore, LookupKey } from "../../stripe/useStripeStore";
import { useLoginState } from "../../studioxr/auth/LoginState";
import { teamAnalytics } from "../teamAnalytics";
import { TeamDetailsStep } from "./steps/TeamDetailsStep";
import { CheckoutStep } from "./steps/CheckoutStep";
import { ReadyStep } from "./steps/ReadyStep";
import { TeamValuePanel } from "./TeamValuePanel";
import {
  CreateTeamErrors,
  CreateTeamFormData,
  FALLBACK_PRICE_PER_SEAT,
  MAX_TEAM_SEATS,
  MIN_TEAM_SEATS,
  computeTotal,
  hasErrors,
  normalizeWebsite,
  validateNameAndDomain,
  validateSeats,
} from "./types";

const STEPS = ["details", "checkout"] as const;
type Step = (typeof STEPS)[number];

const DEFAULT_FORM: CreateTeamFormData = {
  firmName: "",
  domain: "",
  seats: 3,
  interval: "yearly",
};

// The /for-sketchup2 Team toggle opens the wizard on the plan it quoted
// (?billing=monthly|yearly&seats=N). Every other entry has neither param → defaults.
const initialForm = (): CreateTeamFormData => {
  const params = new URLSearchParams(window.location.search);
  const seats = Number.parseInt(params.get("seats") ?? "", 10);
  const billing = params.get("billing");
  return {
    ...DEFAULT_FORM,
    interval: billing === "monthly" || billing === "yearly" ? billing : DEFAULT_FORM.interval,
    seats: Number.isFinite(seats) ? Math.min(MAX_TEAM_SEATS, Math.max(MIN_TEAM_SEATS, seats)) : DEFAULT_FORM.seats,
  };
};

// How long to wait for the webhook to materialize the new firm doc in
// Firestore after Stripe confirms the payment. Anything longer than this
// is exceptional and we surface a "still processing" message to the user
// rather than blocking the navigation indefinitely.
const FIRM_DOC_TIMEOUT_MS = 30_000;

// Wait for the backend webhook to write design_firms/{firmId} after a
// successful Stripe charge. Real-time Firestore listener with a hard
// timeout in case the webhook is slow or fails — the user shouldn't
// stare at "Creating…" forever. A refused read (before the webhook grants
// membership, or a blip) listens again until the timeout.
export const waitForFirmDoc = (db: any, firmId: string): Promise<boolean> =>
  new Promise((resolve) => {
    if (!db) {
      resolve(false);
      return;
    }
    let done = false;
    let unsub = () => {};
    let retry: ReturnType<typeof setTimeout> | undefined;
    const finish = (ok: boolean) => {
      if (done) return;
      done = true;
      clearTimeout(timeout);
      clearTimeout(retry);
      unsub();
      resolve(ok);
    };
    const timeout = setTimeout(() => finish(false), FIRM_DOC_TIMEOUT_MS);
    const listen = () => {
      if (done) return;
      unsub = onSnapshot(
        doc(db, "design_firms", firmId),
        (snap) => {
          if (snap.exists()) finish(true);
        },
        () => {
          if (!done) retry = setTimeout(listen, 1000);
        },
      );
    };
    listen();
  });
// A declined card retries the same PaymentIntent; anything else starts a new attempt (#4334).
const retriesSameIntent = (error: any) => error?.type === "card_error" || error?.type === "validation_error";
const PROCESSING_MESSAGE =
  "Your bank is still processing this payment. Your team appears once it's confirmed, so there's no need to pay again.";
// A retry finds the attempt paid (and waits for that team) or clears it, so nothing is paid twice.
const CONFIRM_LOST_MESSAGE = "We couldn't confirm your payment. Please try again; you won't be charged twice.";
const STILL_SETTING_UP_MESSAGE =
  "Your team is still being set up. Try “Go to your team” again in a moment, or contact studio@mattoboard.com if it doesn't appear.";

/** What /createTeamSubscription returns: the first invoice to confirm, or an attempt already paid. */
type TeamCheckout = {
  kind: "payment" | "active";
  clientSecret?: string;
  amount?: number;
  currency?: string;
  designFirmId?: string;
  subscriptionId: string;
  // kind "active": the existing team's own details.
  firmName?: string;
  seats?: number;
  interval?: string;
};

// Same options as the personal Pro checkout (PaymentComponent): no Stripe dev-tools badge.
const stripePromise = publishableKey
  ? loadStripe(publishableKey, { developerTools: { assistant: { enabled: false } } })
  : null;
// Module-level catch so an early Stripe.js load failure (adblocker, offline,
// privacy browser) doesn't surface as an unhandled rejection (MATTOBOARD-6Q4).
// This module is statically imported via Bar → UserSubscriptionModal,
// so its top-level loadStripe() runs on every page load — even when the
// create-team UI never renders. The wizard's own flow still handles the
// failure for UX when the modal mounts.
stripePromise?.catch((err) => {
  console.error("[CreateTeamWizard] Failed to load Stripe.js:", err);
});

/**
 * Create Team Workspace wizard (Phase B.3).
 *
 * Rendered as modal content inside UserSubscriptionModal (opened via
 * setShowSubscription("createTeam"), e.g. from the workspace switcher) —
 * the same mechanism as the personal Pro payment flow. The host modal
 * supplies the surface; `onClose` dismisses it. Two screens:
 *   1. Team details: name, optional domain, seats, billing plan + live total
 *   2. Stripe PaymentElement + Pay
 * Inviting members (with roles) happens after payment, on the Members page.
 *
 * Submission flow (#4334: one real payment, no €0 card check first):
 *   1. elements.submit() validates the card form
 *   2. POST to /createTeamSubscription with Firebase ID token + form data (no card):
 *      the backend clears earlier unpaid attempts and creates the subscription unpaid
 *   3. stripe.confirmPayment() pays its first invoice (3DS inline)
 *   4. Stripe sends customer.subscription.updated (incomplete → active) to the webhook
 *   5. Backend webhook mints design_firms doc + teams row + permissions.teamsAdmin
 *   6. Frontend listens to design_firms/{designFirmId}; when the doc appears,
 *      clears the workspace active-firms cache, switches the active workspace
 *      to the new team, and shows the ready screen, whose buttons go to
 *      /home/team/members ("Invite your team" opens the invite dialog there)
 */
interface CreateTeamWizardProps {
  onClose: () => void;
  /** Back on the first step. Defaults to closing; the plan overview passes a return to itself. */
  onBack?: () => void;
  /**
   * Reports whether the wizard is in a state the host modal must not
   * dismiss from the shell (backdrop click / Esc): while the payment is
   * being submitted, and after it went through (pendingFirmId set).
   * Dismissing then would unmount the wizard and destroy the
   * paid latch — a reopened wizard could buy a second team.
   */
  onBusyChange?: (busy: boolean) => void;
}

const CreateTeamWizardInner = ({ onClose, onBack, onBusyChange }: CreateTeamWizardProps) => {
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const { currentUser } = useContext(AuthContext);
  const [step, setStep] = useState<Step>("details");
  const [data, setData] = useState<CreateTeamFormData>(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [pendingFirmId, setPendingFirmId] = useState<string | null>(null);
  // The team exists and the user is in it: the "ready" screen replaces checkout.
  const [ready, setReady] = useState(false);

  // Per-seat prices + currency for the wizard's live preview: the team
  // Prices the server picked for this user (regional team Price, else Pro),
  // read out of useStripeStore so display matches what Stripe will charge in
  // the user's local currency. The load starts in the outer wrapper,
  // alongside the SetupIntent.
  const profilePrices = useStripeStore((s: any) => s.profile_prices);
  const monthlyPrice = profilePrices?.[LookupKey.team_monthly];
  const yearlyPrice = profilePrices?.[LookupKey.team_yearly];
  // Until real prices arrive the screen shows placeholders (never the USD
  // fallback) and Continue waits; a stalled load offers a retry.
  const pricesLoaded = [monthlyPrice, yearlyPrice].every(
    (p) => p?.priceID && Number(p?.local_price) > 0,
  );
  const [priceAttempt, setPriceAttempt] = useState(0);
  const [pricesStalled, setPricesStalled] = useState(false);
  useEffect(() => {
    setPricesStalled(false);
    if (pricesLoaded) return undefined;
    const timer = setTimeout(() => setPricesStalled(true), PRICE_LOAD_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [pricesLoaded, priceAttempt]);
  const retryPrices = () => {
    setPriceAttempt((n) => n + 1);
    // Forced: starts a fresh request even if the first one is still hanging.
    useStripeStore.getState().setProductAndPrices?.({ force: true });
  };
  const currency: string =
    monthlyPrice?.currency || yearlyPrice?.currency || "usd";
  const toCents = (raw: any, fallback: number): number => {
    const n = typeof raw === "string" ? Number(raw) : raw;
    return Number.isFinite(n) && n > 0 ? Math.round(n) : fallback;
  };
  const monthlyPerSeat = toCents(
    monthlyPrice?.local_price,
    FALLBACK_PRICE_PER_SEAT.monthly,
  );
  const yearlyPerSeat = toCents(
    yearlyPrice?.local_price,
    FALLBACK_PRICE_PER_SEAT.yearly,
  );
  const pricePerSeat =
    data.interval === "yearly" ? yearlyPerSeat : monthlyPerSeat;
  const totalCents = computeTotal(data.seats, pricePerSeat);
  const chargeCurrency = currency.toLowerCase();
  // The deferred card form follows the total shown; the invoice has the last word on submit.
  useEffect(() => {
    elements?.update({ amount: totalCents, currency: chargeCurrency });
  }, [elements, totalCents, chargeCurrency]);

  const busy = submitting || Boolean(pendingFirmId);
  useEffect(() => {
    onBusyChange?.(busy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busy]);

  const nameErrors = useMemo(() => validateNameAndDomain(data), [data]);
  const seatsErrors = useMemo(() => validateSeats(data), [data]);

  // Which fields the user has interacted with (blurred) or that a failed
  // Next-attempt has revealed. We only surface a field's validation error
  // once it's touched, so the modal doesn't open with a red "required"
  // error on an untouched, empty field.
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const markTouched = (field: string) =>
    setTouched((t) => (t[field] ? t : { ...t, [field]: true }));

  // Errors gated to touched fields — what the step actually renders.
  const visibleNameErrors = useMemo(() => {
    const e: CreateTeamErrors = {};
    if (touched.firmName && nameErrors.firmName) e.firmName = nameErrors.firmName;
    if (touched.domain && nameErrors.domain) e.domain = nameErrors.domain;
    return e;
  }, [touched, nameErrors]);

  const handleChange = (patch: Partial<CreateTeamFormData>) => {
    setData((d) => ({ ...d, ...patch }));
    setSubmitError(null);
  };

  // The card form grows as Stripe's iframe loads; scrolling stays off until it
  // has settled so no scrollbar flashes in and out. Each step opens at the top.
  // Create team waits for the form, so a double-click on Continue can't submit.
  const [cardReady, setCardReady] = useState(false);
  const [paymentReady, setPaymentReady] = useState(false);
  const [paymentLoadFailed, setPaymentLoadFailed] = useState(false);
  const settleTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(settleTimer.current), []);
  // The attempt a declined card retries (same PaymentIntent). It was made with these details
  // (the team name and domain are in its metadata), so a change to any of them drops it.
  const [pendingCheckout, setPendingCheckout] = useState<TeamCheckout | null>(null);
  const firmNameKey = data.firmName.trim();
  const domainKey = normalizeWebsite(data.domain);
  useEffect(() => {
    setPendingCheckout(null);
  }, [data.seats, data.interval, chargeCurrency, totalCents, firmNameKey, domainKey]);
  // Attempts this wizard sent a payment for: a handback of one of them is this wizard's own
  // lost result (count the team once); any other handback was counted where it was paid.
  const confirmedAttempts = useRef(new Set<string>());
  // A payment still processing, or a paid attempt with no team id: Create stays off.
  const [stickyNote, setStickyNote] = useState<string | null>(null);
  const shownError = submitError ?? stickyNote;
  const bodyRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    bodyRef.current?.scrollTo?.({ top: 0 });
  }, [step]);

  const goBack = () => {
    if (busy) return;
    const idx = STEPS.indexOf(step);
    if (idx > 0) {
      // A card error belongs to the payment screen; don't carry it back.
      setSubmitError(null);
      clearTimeout(settleTimer.current);
      setStep(STEPS[idx - 1]);
    } else (onBack ?? onClose)();
  };

  const nameInputRef = useRef<HTMLInputElement>(null);
  const domainInputRef = useRef<HTMLInputElement>(null);
  const goNext = () => {
    // Reveal held-back errors and bring the field into view: the form may be
    // scrolled past it, and Continue must never look like a no-op.
    if (hasErrors(nameErrors)) {
      setTouched((t) => ({ ...t, firmName: true, domain: true }));
      (nameErrors.firmName ? nameInputRef : domainInputRef).current?.focus();
      return;
    }
    if (hasErrors(seatsErrors)) return;
    setSubmitError(null);
    clearTimeout(settleTimer.current);
    setCardReady(false);
    setPaymentReady(false);
    setPaymentLoadFailed(false);
    setStep("checkout");
  };


  // Step 4, once the webhook has minted the firm: refresh the in-memory
  // permissions cache so the newly minted `teamsAdmin`/`teamsDesigner`
  // entries are visible to useWorkspaceContext and TeamLayout. Without this,
  // the workspace list still reflects stale permissions and setActive()
  // silently falls back to Personal. Also refresh the profile so the
  // webhook-written userRole=pro (admin defaults to designer_and_admin
  // → Teams Pro) takes effect immediately — otherwise the Bar still
  // shows "Upgrade to Pro" until the next reload. Then invalidate
  // the workspace's active-firms cache, switch into the new team,
  // and show the ready screen (its buttons route to the members page).
  const enterTeam = async (uid: string, firmId: string) => {
    await Promise.all([
      useLoginState.getState().refreshPermissions(),
      useLoginState.getState().refreshProfile(),
    ]);
    clearActiveFirmsCache(uid);
    // persistActiveWorkspace sets the in-memory store AND writes
    // localStorage, so the new team survives a hard reload immediately
    // after creation (raw setActive would not persist).
    teamAnalytics.workspaceSwitched({ toFirmId: firmId, trigger: "team_created" });
    persistActiveWorkspace(uid, `team:${firmId}`);
    // The ready screen is the creator's welcome to the team.
    useLoginState.getState().markTeamOnboarding(firmId, "welcomeSeenAt");
    teamAnalytics.setupViewed({ firmId, place: "ready" });
    setReady(true);
  };

  const handleSubmit = async () => {
    // Paid latch: once the payment has gone through (pendingFirmId set), never
    // pay again. This guards the post-timeout state below (and any stray double-click).
    if (submitting || pendingFirmId || stickyNote) return;
    setSubmitError(null);
    if (!stripe || !elements) {
      setSubmitError(
        "Payment provider is still loading. Try again in a moment.",
      );
      return;
    }
    if (!currentUser?.uid) {
      setSubmitError("You need to be signed in to create a team.");
      return;
    }
    setSubmitting(true);
    try {
      // Validates the card form before anything is created on Stripe.
      const { error: formError } = await elements.submit();
      if (formError) {
        setSubmitError(formError.message || "Check your card details and try again.");
        return;
      }

      // Nothing is charged until the card is confirmed below, so a refused POST can
      // always be retried; the server clears earlier unpaid attempts first.
      let checkout = pendingCheckout;
      if (!checkout) {
        const idToken = await currentUser.getIdToken();
        const resp = await fetch(createTeamSubscriptionAPIUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            // Backend resolves the per-seat price at the chosen interval.
            // priceId is the Price this wizard displayed: for a regional
            // creator the backend charges the regional team Price and 409s if
            // this id is stale, so display and charge agree.
            firmName: data.firmName.trim(),
            domain: normalizeWebsite(data.domain),
            seats: data.seats,
            interval: data.interval,
            priceId:
              data.interval === "yearly"
                ? yearlyPrice?.priceID
                : monthlyPrice?.priceID,
            currency,
          }),
        });
        const body = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          setSubmitError(
            (body && body.error && body.error.message) ||
              `Failed to create team (HTTP ${resp.status})`,
          );
          return;
        }
        checkout = body as TeamCheckout;
        setPendingCheckout(checkout);
      }

      // kind "active": an earlier attempt turned out paid (its result never reached us).
      if (checkout.kind !== "active") {
        // Charge the invoice's amount/currency (regional price); Stripe wants a new submit after.
        if (checkout.amount !== totalCents || checkout.currency !== chargeCurrency) {
          elements.update({ amount: checkout.amount, currency: checkout.currency });
          const { error: resubmitError } = await elements.submit();
          if (resubmitError) {
            setSubmitError(resubmitError.message || "Check your card details and try again.");
            return;
          }
        }
        let result: any;
        confirmedAttempts.current.add(checkout.subscriptionId);
        try {
          result = await stripe.confirmPayment({
            elements,
            clientSecret: checkout.clientSecret as string,
            redirect: "if_required",
            confirmParams: {
              return_url: `${window.location.origin}/home/concepts`,
            },
          });
        } catch (err) {
          console.error("[CreateTeamWizard] confirmPayment failed:", err);
          setPendingCheckout(null);
          setSubmitError(CONFIRM_LOST_MESSAGE);
          return;
        }
        if (result?.error) {
          setSubmitError(result.error.message || "Your payment could not be completed. Please try again.");
          if (!retriesSameIntent(result.error)) setPendingCheckout(null);
          return;
        }
        const status = result?.paymentIntent?.status;
        if (status === "processing") {
          setStickyNote(PROCESSING_MESSAGE);
          return;
        }
        if (status !== "succeeded") {
          setSubmitError("Your payment could not be completed. Please try again.");
          return;
        }
      }

      // Step 3: wait for the webhook to mint design_firms/{designFirmId}.
      const designFirmId: string | undefined = checkout.designFirmId;
      if (!designFirmId) {
        setStickyNote("Your payment went through but no team id came back. Please contact studio@mattoboard.com.");
        return;
      }
      setPendingFirmId(designFirmId);
      if (checkout.kind === "active") {
        // The team that exists, which may not be what this form asked for: the ready screen shows it.
        const { firmName, seats, interval } = checkout;
        setData((d) => ({
          ...d,
          ...(firmName ? { firmName } : {}),
          ...(seats ? { seats } : {}),
          ...(interval === "monthly" || interval === "yearly" ? { interval } : {}),
        }));
      }
      if (checkout.kind !== "active" || confirmedAttempts.current.has(checkout.subscriptionId)) {
        teamAnalytics.created({
          firmId: designFirmId,
          seats: data.seats,
          interval: data.interval,
          hasDomain: !!data.domain.trim(),
          currency,
        });
      }
      const arrived = await waitForFirmDoc((firebaseHelper as any).db, designFirmId);

      if (!arrived) {
        // Charge succeeded; the webhook is just slow. Leave pendingFirmId set
        // so the Create button stays locked (no second charge) and the footer
        // shows "Go to your team" instead.
        setSubmitError(
          "Your payment went through — your team is being set up. You don't need to create it again; use “Go to your team” in a moment.",
        );
        return;
      }

      await enterTeam(currentUser.uid, designFirmId);
    } catch (err) {
      console.error("[CreateTeamWizard] submit failed:", err);
      // Before the payment nothing is charged, so a retry is safe; after it,
      // pendingFirmId keeps Create locked.
      setSubmitError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  // After a timed-out wait: wait for the team again, then enter it as a normal create does.
  const goToPendingTeam = async () => {
    if (!pendingFirmId || !currentUser?.uid || submitting) return;
    setSubmitError(null);
    setSubmitting(true);
    try {
      if (await waitForFirmDoc((firebaseHelper as any).db, pendingFirmId)) {
        await enterTeam(currentUser.uid, pendingFirmId);
      } else {
        setSubmitError(STILL_SETTING_UP_MESSAGE);
      }
    } catch (err) {
      console.error("[CreateTeamWizard] go to team failed:", err);
      setSubmitError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  // The arrow steps back to details, or on details to the plan overview it came from.
  const showBackArrow = !ready && (step !== "details" || Boolean(onBack));
  // Charge submitted but the webhook-wait timed out: offer the team, never a second charge.
  const showGoToTeam = Boolean(pendingFirmId) && !submitting;
  const teamName = data.firmName.trim();

  const finishReady = (next: "invite" | "go_to_team" | "close") => {
    if (pendingFirmId) teamAnalytics.setupStepStart({ firmId: pendingFirmId, step: next, place: "ready" });
    navigate("/home/team/members", next === "invite" ? { state: { openInvite: true } } : undefined);
    onClose();
  };

  // Fills the modal; only the form body scrolls, so the value panel, the
  // title row and the CTA stay put on short screens.
  return (
    <Stack direction="row" sx={{ width: "100%", flex: 1, minHeight: 0 }}>
      <TeamValuePanel />

      <Stack sx={{ flex: 1, minWidth: 0, minHeight: 0, position: "relative" }}>
        <IconButton
          size="small"
          onClick={ready ? () => finishReady("close") : onClose}
          aria-label="Close"
          // Locked only while the charge is in flight. After a webhook-wait timeout it dismisses
          // without a route change (unlike "Go to your team"), so a board's unsaved edits survive.
          disabled={submitting}
          sx={{ ...tapArea, position: "absolute", top: { xs: 12, sm: 20 }, right: { xs: 12, sm: 20 }, zIndex: 1 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <Box sx={{ flex: "none", px: SIDE_PADDING, pt: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2.5 } }}>
          {/* The title lines up with the fields; from md the arrow hangs in the margin to its left. */}
          <Stack
            direction="row"
            alignItems="center"
            sx={{ position: "relative", maxWidth: CONTENT_MAX_WIDTH, mx: "auto", pr: { xs: 4, sm: 0 } }}
          >
            {showBackArrow && (
              <IconButton
                size="small"
                onClick={goBack}
                aria-label="Back"
                data-testid="create-team-back"
                disabled={busy}
                sx={{
                  ...tapArea,
                  ml: { xs: -1, md: 0 },
                  mr: { xs: 0.5, md: 0 },
                  position: { md: "absolute" },
                  right: { md: "calc(100% + 8px)" },
                  top: { md: "50%" },
                  transform: { md: "translateY(-50%)" },
                }}
              >
                <ArrowBackIcon sx={{ fontSize: 22 }} />
              </IconButton>
            )}
            <Typography
              component="h2"
              sx={{
                fontSize: { xs: 22, sm: 26 },
                fontWeight: 700,
                lineHeight: 1.15,
                letterSpacing: "-0.02em",
              }}
            >
              {ready ? `${teamName} is ready` : step === "details" ? "Create your team" : "Review and pay"}
            </Typography>
          </Stack>
        </Box>

        <Box
          ref={bodyRef}
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: step === "checkout" && stripe && !paymentReady ? "hidden" : "auto",
            px: SIDE_PADDING,
            pb: { xs: 2.5, sm: 3 },
          }}
        >
          <Box sx={{ maxWidth: CONTENT_MAX_WIDTH, mx: "auto" }}>
            {ready && <ReadyStep data={data} />}
            {!ready && step === "details" && (
              <TeamDetailsStep
                data={data}
                errors={{ ...visibleNameErrors, ...(touched.seats ? seatsErrors : {}) }}
                onChange={handleChange}
                onBlur={markTouched}
                monthlyPerSeat={monthlyPerSeat}
                yearlyPerSeat={yearlyPerSeat}
                currency={currency}
                pricesLoaded={pricesLoaded}
                nameInputRef={nameInputRef}
                domainInputRef={domainInputRef}
              />
            )}
            {!ready && step === "checkout" && (
              <CheckoutStep
                data={data}
                pricePerSeat={pricePerSeat}
                currency={currency}
                pendingFirmId={pendingFirmId}
                onPaymentReady={() => {
                  setCardReady(true);
                  clearTimeout(settleTimer.current);
                  settleTimer.current = setTimeout(() => setPaymentReady(true), PAYMENT_SETTLE_MS);
                }}
                onPaymentLoadError={(event) => {
                  const reason = event?.error?.code ?? event?.error?.type ?? "unknown";
                  console.error("[CreateTeamWizard] card form failed to load:", event?.error);
                  sendExceptionToSentry({
                    error: new Error(`Create team: card form failed to load (${reason})`),
                    contextName: "stripeLoadError",
                    context: { ...event?.error },
                  });
                  clearTimeout(settleTimer.current);
                  setPaymentReady(true);
                  setPaymentLoadFailed(true);
                  setSubmitError("The card form couldn't load. Close this and try again.");
                }}
              />
            )}
          </Box>
        </Box>

        {/* One full-width CTA, styled like the personal Pro checkout (ChoosePlan / CheckoutForm). */}
        <Box
          sx={{
            flex: "none",
            borderTop: "1px solid #EFEFF3",
            px: SIDE_PADDING,
            py: { xs: 1.5, sm: 2 },
          }}
        >
          <Box sx={{ maxWidth: CONTENT_MAX_WIDTH, mx: "auto" }}>
            {ready ? (
              <Stack spacing={1}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => finishReady("invite")}
                  data-testid="create-team-invite"
                  sx={ctaSx}
                >
                  Invite your team
                </Button>
                <Button
                  fullWidth
                  color="inherit"
                  onClick={() => finishReady("go_to_team")}
                  data-testid="create-team-ready-go-to-team"
                  sx={{ textTransform: "none", fontSize: 15, color: "#555" }}
                >
                  Go to {teamName}
                </Button>
              </Stack>
            ) : step === "details" ? (
              <Button
                variant="contained"
                fullWidth
                onClick={goNext}
                disabled={!data.firmName.trim() || !data.domain.trim() || hasErrors(seatsErrors) || !pricesLoaded}
                data-testid="create-team-next"
                sx={ctaSx}
              >
                Continue to payment
              </Button>
            ) : showGoToTeam ? (
              // Charge already submitted but the webhook-wait timed out.
              // Do NOT re-offer "Create team" (that would charge again) —
              // wait for the team again and enter it instead.
              <Button
                variant="contained"
                fullWidth
                onClick={goToPendingTeam}
                data-testid="create-team-go-to-team"
                sx={ctaSx}
              >
                Go to your team
              </Button>
            ) : (
              <Button
                variant="contained"
                fullWidth
                onClick={handleSubmit}
                disabled={
                  submitting ||
                  // Once the charge is submitted, lock the button so the
                  // payment can't be sent twice.
                  Boolean(pendingFirmId) ||
                  Boolean(stickyNote) ||
                  !stripe ||
                  !elements ||
                  !cardReady ||
                  paymentLoadFailed ||
                  hasErrors(nameErrors) ||
                  hasErrors(seatsErrors) ||
                  data.seats < MIN_TEAM_SEATS
                }
                data-testid="create-team-submit"
                startIcon={
                  submitting ? (
                    <CircularProgress size={16} sx={{ color: "inherit" }} />
                  ) : undefined
                }
                sx={ctaSx}
              >
                {submitting
                  ? pendingFirmId
                    ? "Setting up your team…"
                    : "Processing payment…"
                  : "Create team"}
              </Button>
            )}
            {shownError && (
              <Typography sx={{ mt: 1.25, fontSize: 14, color: "#b42318" }}>
                {shownError}
              </Typography>
            )}
            {step === "details" && pricesStalled && (
              <Typography sx={{ mt: 1.25, fontSize: 14, color: "#b42318" }}>
                Couldn't load prices.{" "}
                <Link component="button" type="button" onClick={retryPrices} sx={{ fontSize: 14, verticalAlign: "baseline" }}>
                  Try again
                </Link>
              </Typography>
            )}
          </Box>
        </Box>
      </Stack>
    </Stack>
  );
};

// Content column width. At 560px or less Stripe stacks the card number above
// expiry/CVC, matching the personal Pro checkout.
const CONTENT_MAX_WIDTH = 560;
// 48px from md leaves room for the back arrow hanging left of the title.
const SIDE_PADDING = { xs: 2.5, sm: 5, md: 6 };

// Stripe animates the card form's height for a moment after it reports ready.
const PAYMENT_SETTLE_MS = 600;
// How long to wait for real prices before offering a retry.
const PRICE_LOAD_TIMEOUT_MS = 8000;

// Grows the small header icon buttons' tap area to 44px without changing how they look.
const tapArea = { "&::after": { content: '""', position: "absolute", inset: "-7px" } };

// Matches the personal Pro checkout's CTA: black, 18px regular weight, 8px radius.
const ctaSx = {
  py: 1,
  fontSize: 18,
  fontWeight: 400,
  borderRadius: "8px",
  boxShadow: "none",
  bgcolor: "#000",
  color: "#fff",
  "&:hover": { bgcolor: "#222", boxShadow: "none" },
  "&.Mui-disabled": { bgcolor: "#E3E3E8", color: "#9A9AA3" },
};

// Deferred Elements (#4334): nothing exists on Stripe until the card is submitted. The card
// form opens before the plan is known; the inner wizard then keeps it on the total shown.
const INITIAL_ELEMENTS_OPTIONS = {
  mode: "subscription" as const,
  amount: FALLBACK_PRICE_PER_SEAT.yearly * DEFAULT_FORM.seats,
  currency: "usd",
  paymentMethodTypes: ["card"],
};

/**
 * Outer wrapper that wraps the wizard in Stripe Elements. Splitting it from
 * the inner component lets useStripe() / useElements() work in the inner
 * component without props threading.
 */
export const CreateTeamWizard = ({ onClose, onBack, onBusyChange }: CreateTeamWizardProps) => {
  // Start loading prices now (the store dedups calls).
  useEffect(() => {
    useStripeStore.getState().setProductAndPrices?.();
  }, []);

  // No Stripe key: the error gets its own Close (and Back, from the plan overview),
  // since on phones the sheet is full-screen, so there is no backdrop to tap.
  if (!stripePromise) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ position: "relative", flex: 1, p: 6, textAlign: "center" }}>
        {onBack && (
          <IconButton
            size="small"
            onClick={onBack}
            aria-label="Back"
            sx={{ ...tapArea, position: "absolute", top: { xs: 12, sm: 20 }, left: { xs: 12, sm: 20 } }}
          >
            <ArrowBackIcon sx={{ fontSize: 22 }} />
          </IconButton>
        )}
        <IconButton
          size="small"
          onClick={onClose}
          aria-label="Close"
          sx={{ ...tapArea, position: "absolute", top: { xs: 12, sm: 20 }, right: { xs: 12, sm: 20 } }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <Typography color="error">Could not initialize payment form.</Typography>
      </Stack>
    );
  }
  return (
    <Elements stripe={stripePromise} options={INITIAL_ELEMENTS_OPTIONS}>
      <CreateTeamWizardInner onClose={onClose} onBack={onBack} onBusyChange={onBusyChange} />
    </Elements>
  );
};

export default CreateTeamWizard;
